import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GOOGLE_OAUTH_STATE, getGoogleRedirectUri } from '../utils/googleAuth';

const API_BASE_URL = 'https://toxsee.onrender.com';

export default function GoogleAuthorize({ onLogin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const authorizeGoogleUser = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      const oauthError = params.get('error');

      if (oauthError) {
        if (isMounted) {
          setError('Google sign-in was cancelled or denied.');
        }
        return;
      }

      if (!code || state !== GOOGLE_OAUTH_STATE) {
        if (isMounted) {
          setError('Google sign-in is missing a valid authorization response.');
        }
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/google/authorize`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            code,
            state,
            redirectUri: getGoogleRedirectUri()
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Google sign-in failed.');
        }

        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        if (data.profile) {
          localStorage.setItem('userProfile', JSON.stringify(data.profile));
        } else {
          localStorage.removeItem('userProfile');
        }

        if (isMounted) {
          onLogin(data.user);
          navigate('/');
        }
      } catch (authorizeError) {
        if (isMounted) {
          setError(authorizeError.message || 'Google sign-in failed.');
        }
      }
    };

    authorizeGoogleUser();

    return () => {
      isMounted = false;
    };
  }, [location.search, navigate, onLogin]);

  return (
    <main style={{
      minHeight: '100vh',
      background: 'var(--page-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <section style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        padding: '40px',
        maxWidth: '460px',
        width: '100%',
        textAlign: 'center',
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 16px',
          borderRadius: '50%',
          background: 'var(--brand-primary-light)',
          color: 'var(--teal)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '22px',
          fontWeight: '700',
        }}>
          G
        </div>

        <h1 style={{
          margin: '0 0 12px',
          fontSize: '24px',
          color: 'var(--teal-dark)',
        }}>
          {error ? 'Google Sign-In Error' : 'Authorizing Google Sign-In'}
        </h1>

        <p style={{
          margin: 0,
          color: '#666',
          fontSize: '14px',
          lineHeight: 1.6,
        }}>
          {error || 'We’re finishing your sign-in and loading your account.'}
        </p>

        {error && (
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              marginTop: '24px',
              padding: '12px 18px',
              background: 'var(--teal)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Back to Sign In
          </button>
        )}
      </section>
    </main>
  );
}
